import {
  Button,
  Popup,
  SafeHtmlContent,
  Text
} from '@yes.technology/react-toolkit'
import { kebabCase } from 'lodash'
import React, { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getApplicationSystemInfo } from 'shared/utils'
import { Container, Content, Image } from './WelcomeModal.styles'
import { WelcomeModalProps } from './WelcomeModal.types'

export const DISMISSED_WELCOME_SITEMODELS_KEY = 'dismissed-welcome-sitemodels'

export const DISMISSED_GLOBAL_WELCOME_SITEMODELS_KEY =
  'dismissed-global-welcome-sitemodels'

const getDismissedWelcomeSitemodels = (key: string): string[] => {
  try {
    const ids = JSON.parse(window.localStorage.getItem(key) || '[]')

    return Array.isArray(ids) && ids.every((id) => typeof id === 'string')
      ? ids
      : []
  } catch (e) {}

  return []
}

const getApplicationSystemState = (key: string): boolean => {
  try {
    const state: { persisted?: boolean } = JSON.parse(
      window.localStorage.getItem(key) || '{}'
    )

    return state?.persisted || false
  } catch (e) {
    return false
  }
}

const appendDismissedWelcomeSitemodel = (
  key: string,
  sitemodelUuid: string
) => {
  const previousIds = getDismissedWelcomeSitemodels(key)
  const newIds = [...previousIds, sitemodelUuid]
  window.localStorage.setItem(key, JSON.stringify(newIds))
}

const persistApplicationSystemState = (key: string) => {
  window.localStorage.setItem(
    key,
    JSON.stringify({
      persisted: true
    })
  )
}

type WelcomeModalConfig = {
  storageKey: string
  getOpenState: (applicationSystemKey: string, sitemodelUuid: string) => boolean
  handleClose: (storageKey: string, sitemodelUuid: string) => void
  getStyle: () => React.CSSProperties
}

const createGlobalConfig = (
  applicationSystemKey: string
): WelcomeModalConfig => ({
  storageKey: applicationSystemKey,
  getOpenState: (key: string) => !getApplicationSystemState(key),
  handleClose: (storageKey: string) => {
    persistApplicationSystemState(storageKey)
  },
  getStyle: () => ({ zIndex: 9999 })
})

const createSitemodelConfig = (): WelcomeModalConfig => ({
  storageKey: DISMISSED_WELCOME_SITEMODELS_KEY,
  getOpenState: (_applicationSystemKey: string, sitemodelUuid: string) => {
    const dismissedList = getDismissedWelcomeSitemodels(
      DISMISSED_WELCOME_SITEMODELS_KEY
    )
    return !dismissedList.includes(sitemodelUuid)
  },
  handleClose: (storageKey: string, sitemodelUuid: string) => {
    appendDismissedWelcomeSitemodel(storageKey, sitemodelUuid)
  },
  getStyle: () => ({ zIndex: 9999 })
})

const getWelcomeModalConfig = (
  isGlobal: boolean,
  applicationSystemKey: string
): WelcomeModalConfig => {
  if (isGlobal) {
    return createGlobalConfig(applicationSystemKey)
  }
  return createSitemodelConfig()
}

type UseWelcomeModalProps = {
  sitemodelUuid: string
  isGlobal: boolean
}

const useWelcomeModal = ({ sitemodelUuid, isGlobal }: UseWelcomeModalProps) => {
  const applicationSystem = getApplicationSystemInfo()

  const applicationSystemKey = kebabCase(
    applicationSystem.ownerDes || 'global-welcome-modal'
  )

  const config = getWelcomeModalConfig(isGlobal, applicationSystemKey)

  const getOpenState = useCallback(() => {
    const currentConfig = getWelcomeModalConfig(isGlobal, applicationSystemKey)
    return currentConfig.getOpenState(applicationSystemKey, sitemodelUuid)
  }, [applicationSystemKey, sitemodelUuid, isGlobal])

  const [isOpen, setIsOpen] = useState(getOpenState())

  const close = useCallback(() => {
    const currentConfig = getWelcomeModalConfig(isGlobal, applicationSystemKey)
    currentConfig.handleClose(currentConfig.storageKey, sitemodelUuid)
    setIsOpen(false)
  }, [isGlobal, applicationSystemKey, sitemodelUuid])

  const style = config.getStyle()

  return {
    isOpen,
    close,
    style
  }
}

const WelcomeModal = ({
  props: {
    title,
    image,
    content,
    main_button: mainButton,
    dismiss_button: dismissButton,
    isGlobal = false
  }
}: WelcomeModalProps) => {
  const { sitemodelUuid = '' } = useParams<{ sitemodelUuid: string }>()
  const { isOpen, close, style } = useWelcomeModal({ sitemodelUuid, isGlobal })

  return (
    <Popup {...{ isOpen }} onClose={close} heightClass='regular' style={style}>
      <Container>
        <Text variant='label-required-04'>{title}</Text>
        <Image src={image.url} alt={image.alt || ''} />
        <Content>
          <SafeHtmlContent html={content} />
        </Content>
        {mainButton && (
          <a
            href={mainButton.url}
            target={mainButton.target}
            rel={
              mainButton.target === '_blank' ? 'noopener noreferrer' : undefined
            }
            onClick={close}
            tabIndex={-1}
          >
            <Button>{mainButton.label}</Button>
          </a>
        )}
        <Button onClick={close} variant={dismissButton?.variant}>
          {dismissButton.label}
        </Button>
      </Container>
    </Popup>
  )
}

export default WelcomeModal
